import type { CharacterSheetInterface } from '@/types/characters';
import type { AppliedConfiguration, CarriedPayloadApplication, ContainerEffect, SwadeModConfigurationInterface, SwadeModificationInterface } from '@/types/items';
import { authoredTargetMode, CONTAINER_PATHS, type ContainerArrayName, effectTargets, isElementEmpty, isRedirectableElement, resolveContainerTargets, supportedContainerPath } from '@/appdata/container-targets';
import { resolvedModificationUuid } from '@/appdata/applied-configuration';
import { powerDetailBase, resolveContainerDetails, resolvePowerDetails } from '@/appdata/container-details';
import DieCode from '@/types/die-code';

/**
 * The carried modifications a container modification transfers to its target (spec 2026-09-10
 * carried modifications). Pure: the store injects its in-use predicate. Nothing here applies
 * anything — syncCarriedPayloads does, from what this module says is desired.
 */

/** The configurations of the well-formed entries in `carry`, tolerating anything a hand-written blob holds. */
export function carriedEntries(configuration: SwadeModConfigurationInterface | null | undefined): SwadeModConfigurationInterface[] {
    const carry = configuration?.carry;
    if (!Array.isArray(carry)) return [];
    return carry
        .map((entry) => (entry as any)?.configuration)
        .filter((c): c is SwadeModConfigurationInterface => c !== null && typeof c === 'object');
}

/** The build-option paths a container may carry (ruling 2026-09-14); the rest of buildModifications.labels.path stays off the list. */
const CARRIABLE_BUILD_OPTIONS: ReadonlySet<string> = new Set(['buildPoints', 'additionalAttribute', 'edge', 'hindrance', 'linkedAttribute', 'startingFunds']);

/**
 * Paths a carried entry may use — derived, never copied: every character path the label tree offers
 * except container.* and the choice set, then the carriable build-option paths the tree offers, each
 * in label-tree order. The two kinds live in maps that share no key (the character configurator's
 * select reads modifications.labels.path, the build-option configurator's reads
 * buildModifications.labels.path), and this one list feeds both selects.
 */
export function carriablePaths(labels: unknown): string[] {
    const data = (labels as any)?.swade?.data;
    const keysOf = (map: unknown): string[] => (map && typeof map === 'object' ? Object.keys(map as object) : []);
    const characterPaths = keysOf(data?.modifications?.labels?.path).filter((key) =>
        !Object.prototype.hasOwnProperty.call(CONTAINER_PATHS, key) && key !== 'character.choiceSet');
    const buildOptionPaths = keysOf(data?.buildModifications?.labels?.path).filter((key) => CARRIABLE_BUILD_OPTIONS.has(key));
    return [...characterPaths, ...buildOptionPaths];
}

/** A carried entry whose own path would prompt on an empty element cannot be applied as authored (spec §2.2). */
export const needsFixedElement = (configuration: SwadeModConfigurationInterface): boolean =>
    (configuration.path === 'character.edges' || configuration.path === 'character.skills')
    && isElementEmpty(configuration.element);

/**
 * A carried entry the player answers when the rung is picked (spec 2026-09-22 v2.2 §3.2): a container
 * path other than the power's own (`container.powers` is excluded whatever the owner: on a power it is
 * self mode and stays here, and no other owner authors it), with a redirectable element and not All
 * Specified — or a choice set, whose options are the effects and whose answer is the player's (owner's
 * ruling 2026-09-24, Additional Sense; supersedes ruling 33). Its application is a RECORD on the source
 * container, applied by the record loop — so this ledger leaves it alone, silently: no row, no warning.
 */
export const resolvedByRecord = (configuration: SwadeModConfigurationInterface): boolean =>
    configuration.path === 'character.choiceSet'
    || (supportedContainerPath(configuration.path) !== undefined
        && configuration.path !== 'container.powers'
        && isRedirectableElement(configuration.element)
        && authoredTargetMode(configuration) !== 'allSpecified');

// Identity of an entry for the ledger: everything but its labels, with keys sorted so two equal
// configurations written in different orders agree.
const normalisedEntry = (entry: SwadeModConfigurationInterface): Record<string, unknown> => {
    const { label: _label, summary: _summary, ...rest } = entry;
    return Object.fromEntries(Object.entries(rest).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
};

/**
 * A carried entry authored with a blank value borrows its OWNER's value for the same detail (ruling 47,
 * 2026-09-24; first for damage, 2026-09-23: Melee Attack's Special Weapon lends the melee attack bonus
 * to the picked weapon, its Unarmed wrapper lends the power's damage and AP to every Unarmed) — and so
 * does the effect ITSELF since the owner's ruling of 2026-09-28 (Deadeye), for the owner's RESOLVED
 * value (see `consider`). The blank is the whole signal: no flag, no new detail.
 */
export const borrowsOwnerValue = (entry: SwadeModConfigurationInterface | null | undefined): boolean =>
    typeof entry?.detail === 'string' && entry.detail !== ''
    && (entry.value === null || entry.value === undefined || (typeof entry.value === 'string' && entry.value.trim() === ''));

/**
 * The value an owner lends for one detail, or null. For a CARRIED entry `damage` keeps the base — the
 * owner's own level copy before any override, else its catalog's (the one rule powerDetailBase spells out
 * for a power; an item has no copy and reads its catalog): Claws picked on the same Melee Attack is another
 * attack form that must not ride along. The effect ITSELF transfers the owner's RESOLVED damage, its own
 * overrides included (`damage: 'resolved'`; owner's ruling 2026-09-28: Deadeye's +d6 is the power's own
 * damage, and Deadly's re-rating to d10 must reach the picked bow). Either way dice and flats only: an
 * attribute token (`Str`) is the target's own base and never travels. Every OTHER detail borrows the
 * owner's RESOLVED value, its own overrides included — a power's AP exists only as an override. The entry's
 * operator is untouched: `add` adds the borrowed value to the target's own, `alter` replaces it. Null for
 * an owner that is not a power or an item, that has no such value, or that is the target itself: a
 * container's own self-mode blank would double its die.
 */
export function lentValue(
    owner: unknown,
    ownerArray: ContainerArrayName | null,
    targetUuid: string,
    detail: string,
    damage: 'base' | 'resolved' = 'base',
): string | number | null {
    if (owner === null || typeof owner !== 'object' || (owner as { uuid?: unknown }).uuid === targetUuid) return null;
    const resolved: unknown = ownerArray === 'powers' ? resolvePowerDetails(owner as never)
        : ownerArray === 'equipment' ? resolveContainerDetails(owner as never)
        : null;
    if (detail === 'damage') {
        const formula: unknown = damage === 'resolved'
            ? (resolved as { damage?: unknown } | null)?.damage
            : powerDetailBase(owner as never).damage;
        if (typeof formula !== 'string' || formula.trim() === '') return null;
        const terms = new DieCode(formula).getTerms().filter((term) => term.kind !== 'token');
        return terms.length > 0 ? DieCode.fromTerms(terms).getFormula() : null;
    }
    // Own-property only: `detail` is author data, and a bare index would hand back an inherited member.
    if (resolved === null || typeof resolved !== 'object' || !Object.prototype.hasOwnProperty.call(resolved, detail)) return null;
    const value = (resolved as Record<string, unknown>)[detail];
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * The ledger key (spec §3.2): owner, target, the outer modification's identity, the entry's index and the
 * entry itself. The effect's OWN transfer row (owner's ruling 2026-09-28) takes index -1, below every
 * carried entry's.
 */
export const TRANSFER_INDEX = -1;

/**
 * The effect as its transfer row applies it to ONE target (owner's ruling 2026-09-28): self-mode — no element,
 * no All Specified flag, so the row lands on the target it was made for and nowhere else — carrying nothing,
 * with the owner's value in place of the blank. Label and summary ride along for the ledger reader.
 */
export const transferredEffect = (outer: SwadeModConfigurationInterface, value: string | number): SwadeModConfigurationInterface => {
    const { element: _element, applyToAll: _applyToAll, carry: _carry, ...rest } = outer as SwadeModConfigurationInterface & { applyToAll?: unknown, carry?: unknown };
    return { ...rest, value } as SwadeModConfigurationInterface;
};

export const carriedApplicationKey = (
    ownerUuid: string,
    targetUuid: string,
    outerKey: string,
    index: number,
    entry: SwadeModConfigurationInterface,
): string => JSON.stringify([ownerUuid, targetUuid, outerKey, index, normalisedEntry(entry)]);

export interface DesiredCarriedApplication {
    key: string;
    ownerUuid: string;
    targetUuid: string;
    configuration: SwadeModConfigurationInterface;
    desired: number;
}

export interface SkippedCarriedEntry {
    ownerUuid: string;
    outerKey: string;
    index: number;
    configuration: SwadeModConfigurationInterface;
}

/**
 * What every carried entry should currently be applied as, from full state (spec §3.1–3.2):
 * desired = units(target) × (owner === target ? 1 : units(owner)). `units` is the store's
 * predicate — an equipment container's multiplier while in use, else 0; 1 while present for a
 * skill, edge or hindrance. A record owner that is not a container (a framework, an ancestry, the
 * world) counts as 1. Three sources: records of any owner, resolved to their targets; each present
 * container's own direct container.* modifications in self mode; and every All Specified entry of
 * the effects ledger, resolved now (spec 2026-09-13 §4.3). `units` also receives the carried entry,
 * so a host can answer per path (spec 2026-09-22 §3.3: a container-detail effect on a power applies
 * once per pick, a character effect once per level). A record's rows are further multiplied by
 * `repeats(record)` — the points invested in a repeatable modifier (owner's ruling 2026-09-23, spec
 * v2.2 §11, ruling 45: Armor Piercing ×3 at +2 AP per point is AP 6); the caller derives it, this
 * module only multiplies, and the default is one for every record. An entry with a blank value
 * borrows its OWNER's value for the same detail (borrowsOwnerValue / lentValue, ruling 47),
 * materialised into the row's configuration — and so into its key — so a change of the owner's value
 * re-keys the row and the sync rolls the old one back; an entry with nothing to borrow yields no row.
 * The effect ITSELF, when its value is blank, is one more row per target with the owner's RESOLVED
 * value (owner's ruling 2026-09-28, Deadeye): its own ledger entry, valued null, folds nothing, and
 * this row is what carries the power's damage — re-rated by its modifiers — to the weapons it picked.
 */
export function desiredCarriedApplications(
    character: CharacterSheetInterface,
    units: (container: any, entry?: SwadeModConfigurationInterface) => number,
    repeats: (record: AppliedConfiguration) => number = () => 1,
): { desired: DesiredCarriedApplication[], skipped: SkippedCarriedEntry[] } {
    const desired: DesiredCarriedApplication[] = [];
    const skipped: SkippedCarriedEntry[] = [];

    const containerByUuid = new Map<string, any>();
    // Which array an owner sits in decides whose resolver lends its value (lentValue).
    const arrayOf = new Map<string, ContainerArrayName>();
    for (const arrayName of new Set(Object.values(CONTAINER_PATHS))) {
        const containers = (character as any)?.[arrayName];
        if (!Array.isArray(containers)) continue;
        for (const container of containers) {
            if (typeof container?.uuid !== 'string') continue;
            containerByUuid.set(container.uuid, container);
            arrayOf.set(container.uuid, arrayName);
        }
    }

    const consider = (ownerUuid: string, outerKey: string, outer: SwadeModConfigurationInterface, targets: any[], factor: number = 1) => {
        const owner = containerByUuid.get(ownerUuid);
        const ownerArray = arrayOf.get(ownerUuid) ?? null;
        const ownerUnitsFor = (targetUuid: string): number => owner === undefined || targetUuid === ownerUuid ? 1 : units(owner);
        // The effect ITSELF, when its value is blank (owner's ruling 2026-09-28, Deadeye): a container effect
        // authored on a power with no value transfers the power's RESOLVED value for its detail to its targets
        // — Deadeye's +d6 is the power's own damage, Deadly re-rates it to d10 on the power, and the bow the
        // player picked reads 2d6+d10 — folded by the effect's own operator and value type. One self-mode row
        // per target, keyed on the value, so a re-rating re-keys it and an All Specified effect lands once per
        // container; the effect's own ledger entry, valued null, folds nothing. A CARRIED blank keeps ruling 47
        // below: its damage borrows the base, and Claws stays off Special Weapon's target.
        if (borrowsOwnerValue(outer)) {
            for (const target of targets) {
                if (typeof target?.uuid !== 'string') continue;
                const lent = lentValue(owner, ownerArray, target.uuid, outer.detail as string, 'resolved');
                if (lent === null) continue;
                const configuration = transferredEffect(outer, lent);
                desired.push({
                    key: carriedApplicationKey(ownerUuid, target.uuid, outerKey, TRANSFER_INDEX, configuration),
                    ownerUuid,
                    targetUuid: target.uuid,
                    configuration,
                    desired: units(target, outer) * ownerUnitsFor(target.uuid) * factor,
                });
            }
        }
        carriedEntries(outer).forEach((entry, index) => {
            if (resolvedByRecord(entry)) return;
            if (needsFixedElement(entry)) {
                skipped.push({ ownerUuid, outerKey, index, configuration: entry });
                return;
            }
            // A carried entry of the OWNER's own kind lands on the owner when the effect's targets are of
            // another kind (ruling 49, 2026-09-24, generalised from powers to every kind 2026-09-25):
            // Reach's item effect carries the power's cost for that reach, an edge's item effect may carry
            // the edge's own trapping — on the item either would resolve to nothing. One row, however
            // many targets the effect reaches, and one even when it reaches none: the choice was made. An
            // entry of the TARGETS' kind still rides to them, whoever owns it (Special Weapon's nested
            // damage lands on the weapon; a rung's own carry targets the power), and so does one of neither
            // kind, inert there as before.
            const entryArray = supportedContainerPath(entry.path);
            const ownKind = entryArray !== undefined && entryArray !== supportedContainerPath(outer.path) && owner !== undefined && entryArray === ownerArray;
            const rowTargets = ownKind ? [owner] : targets;
            for (const target of rowTargets) {
                if (typeof target?.uuid !== 'string') continue;
                const ownerUnits = ownerUnitsFor(target.uuid);
                let configuration = entry;
                if (borrowsOwnerValue(entry)) {
                    const lent = lentValue(owner, ownerArray, target.uuid, entry.detail as string);
                    // Nothing to borrow, nothing to apply (ruling 47): no inert row, and no row to roll back
                    // when the settling pass that produces the value comes round.
                    if (lent === null) continue;
                    configuration = { ...entry, value: lent };
                }
                desired.push({
                    key: carriedApplicationKey(ownerUuid, target.uuid, outerKey, index, configuration),
                    ownerUuid,
                    targetUuid: target.uuid,
                    configuration,
                    desired: units(target, entry) * ownerUnits * factor,
                });
            }
        });
    };

    const records: AppliedConfiguration[] = Array.isArray((character as any)?.additionalData?.choices)
        ? (character as any).additionalData.choices
        : [];
    for (const record of records) {
        const configuration = record?.configuration;
        if (typeof record?.uuid !== 'string' || !supportedContainerPath(configuration?.path)) continue;
        // An All Specified record — a power's own choice set answered with an All Specified option
        // (Reach) — applies as a LEDGER entry, which the third source walks; walking the record too
        // would carry its cost twice (ruling 49, 2026-09-24).
        if (authoredTargetMode(configuration) === 'allSpecified') continue;
        const outerKey = resolvedModificationUuid(record);
        if (!outerKey) continue;
        consider(record.uuid, outerKey, configuration, resolveContainerTargets(configuration, character, record.uuid).targets, repeats(record));
    }

    for (const container of containerByUuid.values()) {
        const catalog = container.selection ?? container.skill ?? container.hindrance;
        const modifications: SwadeModificationInterface[] = Array.isArray(catalog?.modifications) ? catalog.modifications : [];
        for (const mod of modifications) {
            const configuration = mod?.configuration;
            if (typeof mod?.uuid !== 'string' || !supportedContainerPath(configuration?.path) || configuration.element !== undefined) continue;
            consider(container.uuid, mod.uuid, configuration, resolveContainerTargets(configuration, character, container.uuid).targets);
        }
    }

    // Third source (spec 2026-09-13 §4.3): every All Specified entry in the effects ledger, resolved
    // to the containers it hits NOW through the same resolver the override rebuild uses, and fed to
    // the same closure — units, owner-equals-target, the key and the fixed-element skip are all
    // `consider`'s. De-duplicated by (source, modification key): source ticking writes one entry
    // per unit of the owner, and units(owner) inside `consider` already carries that quantity, so
    // feeding every entry would square it and collide the keys.
    const ledger: unknown = (character as any)?.additionalData?.containerEffects;
    const seenDynamic = new Set<string>();
    for (const entry of (Array.isArray(ledger) ? ledger as ContainerEffect[] : [])) {
        if (authoredTargetMode(entry?.configuration) !== 'allSpecified') continue;
        if (typeof entry?.sourceUuid !== 'string' || typeof entry?.sourceModUuid !== 'string') continue;
        const identity = JSON.stringify([entry.sourceUuid, entry.sourceModUuid]);
        if (seenDynamic.has(identity)) continue;
        seenDynamic.add(identity);
        consider(entry.sourceUuid, entry.sourceModUuid, entry.configuration, effectTargets(character, entry));
    }

    return { desired, skipped };
}

/**
 * The walkers' view of the ledger (spec §3.4): every applied row for the path and element, each
 * carrying `multiplier` = its count. The ledger, not this function, decided whether each is in use.
 */
export type CarriedAppliedConfig = SwadeModConfigurationInterface & { multiplier: number };

/**
 * Every applied row of the ledger, whatever its path, as the walkers read one: the configuration
 * carrying `multiplier` = its count. A row without an object configuration or with a non-positive
 * count is not applied and is skipped. The store's shared iterator (iterateModifications) filters
 * these by its own predicate; carriedAppliedConfigs below is the path-and-element view of the same.
 */
export function carriedRows(ledger: unknown): CarriedAppliedConfig[] {
    if (!Array.isArray(ledger)) return [];
    return (ledger as CarriedPayloadApplication[])
        .filter((row) => row?.configuration !== null && typeof row?.configuration === 'object'
            && typeof row.count === 'number' && row.count > 0)
        .map((row) => ({ ...row.configuration, multiplier: row.count }));
}

export function carriedAppliedConfigs(ledger: unknown, path: string, element: string): CarriedAppliedConfig[] {
    return carriedRows(ledger).filter((row) => row.path === path && row.element === element);
}

/**
 * For the build-point walkers: the applied buildPoints rows for the element. characterBuildPointModifier
 * and perkPointModifier scale by `multiplier`; the language-slot derivation in updateBuildValidation
 * deliberately ignores it.
 */
export function carriedBuildOptionConfigs(ledger: unknown, element: string): SwadeModConfigurationInterface[] {
    return carriedAppliedConfigs(ledger, 'buildPoints', element);
}
